import { useEffect, useState } from 'react';
import { useHref } from 'react-router-dom';
import { DateTime, DurationLike } from 'luxon';

import {
	AUTH_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	parseJwt,
} from '../../../views/home/auth/Authentication.hooks';
import {
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	APP_USER_ID,
	BUILD_NUMBER,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
} from '../../../constants';

import config from '../../../config';
import { AuthCognito } from '../../../config.type';
import { Logger } from '../../../utils/Logger';

const logger = new Logger('Token', {
	log: 'rgb(88, 86, 214)',
	debug: 'rgb(54, 52, 163)',
	info: 'rgb(125, 122, 255)',
	error: 'rgb(211, 15, 69)',
});

export function useAuth() {
	const basename = useHref('/callback');
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (config?.app?.auth?.type !== 'cognito') {
		return;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	useEffect(() => {
		if (isAdmin) {
			return;
		}

		if (!isAuthenticated) {
			location.href = redirectUrl;
			return;
		}
		// At this point we know that the user is authenticated
		const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

		// If one of this two tokens are missing, clear everything and redirect
		if (!accessToken || !refreshToken) {
			clearTokens(redirectUrl);
			return;
		}

		// In here the access token is expired but the app is offline
		if (!navigator.onLine) {
			return;
		}

		// JWT Parse Data
		const accessTokenData = parseJwt(accessToken);
		const refreshTokenData = parseJwt(refreshToken);

		// Calculate expiration dates
		const accessExpirationDate = DateTime.fromSeconds(accessTokenData.exp);
		const refreshExpirationDate = DateTime.fromSeconds(
			refreshTokenData.exp,
		);

		const now = DateTime.now();

		// If now is larger than refresh expiration date clear the tokens
		if (now >= refreshExpirationDate) {
			clearTokens(redirectUrl);
			return;
		}

		// If true the user access token is valid
		if (now < accessExpirationDate) {
			return;
		}

		refreshAccessToken(refreshToken)
			.then((accessExpirationDate) => {
				logger.log(
					`Token refreshed successfully: '${accessExpirationDate}'`,
				);
			})
			.catch((err) => {
				logger.error(err);
				alert(err.message);
				// clearTokens(redirectSignUpUrl);
			});
	}, []);
}

export function tryRefreshToken(
	redirect: string,
	durationOffset?: DurationLike,
) {
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return;
	}
	if (!isAuthenticated) {
		return;
	}
	// In here the access token is expired but the app is offline
	if (!navigator.onLine) {
		return;
	}

	// At this point we know that the user is authenticated
	const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
	const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
	const accessTokenExp = localStorage.getItem(ACCESS_TOKEN_EXPIRATION_KEY);
	const refreshTokenExp = localStorage.getItem(REFRESH_TOKEN_EXPIRATION_KEY);

	// If one of this two tokens are missing, clear everything and redirect
	if (!accessToken || !refreshToken || !accessTokenExp || !refreshTokenExp) {
		logger.debug(
			`One of the two tokens is missing and i will kick you out`,
		);
		clearTokens(redirect);
		return;
	}

	// Calculate expiration dates
	const accessExpirationDate = durationOffset
		? DateTime.fromISO(accessTokenExp).minus(durationOffset)
		: DateTime.fromISO(accessTokenExp);
	const refreshExpirationDate = DateTime.fromISO(refreshTokenExp);

	const now = DateTime.now();

	// If now is larger than refresh expiration date clear the tokens
	if (now >= refreshExpirationDate) {
		logger.debug(`THE REDIRECT TOKEN EXPIRED AND I WILL KICK YOU OUT`);
		clearTokens(redirect);
		return;
	}

	// If true the user access token is valid
	if (now < accessExpirationDate) {
		const diff = accessExpirationDate.diff(now);
		logger.debug(
			`The token is valid and is going to expire in `,
			msToTime(diff.toObject().milliseconds),
		);
		return;
	}

	refreshAccessToken(refreshToken)
		.then((accessExpirationDate) => {
			const accessExpiration = DateTime.fromJSDate(
				new Date(accessExpirationDate),
			);
			const now = DateTime.now();
			const diff = accessExpiration.diff(now);
			logger.log(`Token refreshed successfully`);
			logger.debug(
				`Token is going to expire at: ${accessExpirationDate}`,
			);

			logger.debug(
				`The token is going to expire in: ${msToTime(
					diff.toObject().milliseconds,
				)}`,
			);
		})
		.catch((err) => {
			// THERE IS AN ERROR
			logger.error(err);
			clearTokens(redirect);
		});
}

export function logOut(basename: string) {
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	if (!isAuthenticated) {
		return;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		`logout_uri=${redirectUri}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/logout?', redirectSections].join(
		'',
	);

	location.href = redirectUrl;
}

export function clearTokens(redirect?: string) {
	const keys = [
		AUTH_KEY,
		USER_ROLE,
		ACCESS_TOKEN_KEY,
		ACCESS_TOKEN_EXPIRATION_KEY,
		REFRESH_TOKEN_KEY,
		ACCESS_TOKEN_EXPIRE_AT_KEY,
		REFRESH_TOKEN_EXPIRATION_KEY,
		APP_USER_ID,
	];
	for (const k of keys) {
		if (localStorage.getItem(k)) {
			localStorage.removeItem(k);
		}
	}

	if (redirect) {
		location.href = redirect;
	}
}

export async function refreshAccessToken(token: string): Promise<string> {
	const queryParams = new URLSearchParams({
		refresh_token: token,
	}).toString();
	const url = `${config.build.host}/auth/token?${queryParams}`;
	const rawResponse = await fetch(url, {
		method: 'GET',
		headers: {
			'app-key': config.build.appKey,
			'Content-Type': 'application/json',
		},
	});
	const content = await rawResponse.json();
	const { access_token, expires } = content;

	// Calculate expiration dates
	const accessExpirationDate = DateTime.now()
		.plus({ seconds: expires })
		.toISO();

	localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
	localStorage.setItem(ACCESS_TOKEN_EXPIRATION_KEY, accessExpirationDate);
	return accessExpirationDate;
}

export function useLogout(): boolean {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	useEffect(() => {
		const keys = [
			AUTH_KEY,
			USER_ROLE,
			ACCESS_TOKEN_KEY,
			ACCESS_TOKEN_EXPIRATION_KEY,
			REFRESH_TOKEN_KEY,
			ACCESS_TOKEN_EXPIRE_AT_KEY,
			REFRESH_TOKEN_EXPIRATION_KEY,
			APP_USER_ID,
		];
		for (const k of keys) {
			if (localStorage.getItem(k)) {
				localStorage.removeItem(k);
			}
		}
		setIsLoading(false);
	}, []);

	return isLoading;
}

/**
 * React hook that reset everything if the build changes
 *
 *
 */
export function useRefreshOnBuild() {
	useEffect(() => {
		const buildNumber = localStorage.getItem(BUILD_NUMBER);
		if (!buildNumber) {
			console.log(`Build number was never set`);
			localStorage.setItem(BUILD_NUMBER, `${config.build.buildNumber}`);
			clearTokens();
			location.reload();
			return;
		}
		if (buildNumber !== `${config.build.buildNumber}`) {
			console.log(`This build is different than the existing one`);
			localStorage.setItem(BUILD_NUMBER, `${config.build.buildNumber}`);
			clearTokens();
			location.reload();
			return;
		}
		console.log(`The app has the same build`);
	}, []);
}

function msToTime(duration?: number): string {
	if (!duration) {
		return '';
	}
	const milliseconds: any = Math.floor((duration % 1000) / 100);
	let seconds: number | string = Math.floor((duration / 1000) % 60);
	let minutes: number | string = Math.floor((duration / (1000 * 60)) % 60);
	let hours: number | string = Math.floor((duration / (1000 * 60 * 60)) % 24);

	hours = hours < 10 ? '0' + hours : hours;
	minutes = minutes < 10 ? '0' + minutes : minutes;
	seconds = seconds < 10 ? '0' + seconds : seconds;

	return hours + ':' + minutes + ':' + seconds + '.' + milliseconds;
}
